import { useErrorHandlingSlice } from 'app/error/slice';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import useApi from 'services/mavapi/useApi';
import {
  ManageCoachService,
  CoachListWrapper,
  AdminCoachInfo,
  GeneralLookup,
  ChangeCoachActiveStatusRequest,
  Coach,
  PendingRidersList,
  CoachService,
  ApproveRiderRequest,
  CoachInfo,
  SendInsurancePdf,
  InsuranceApplicationPdf,
} from 'services/openapi';
import { useNavigateToRoute } from 'utils/common';
import isApiError from 'utils/isApiError';

const useCoachApi = () => {
  const [loading, setLoading] = useState(false);
  const [coachDataLoading, setCoachDataLoading] = useState(false);
  const [, setError] = useState<unknown | null>();
  const { error: apiError, handleRequest } = useApi();
  const { action } = useParams();
  const redirect = useNavigateToRoute();

  const {
    manageCoachGetCoaches,
    manageCoachGetCoachInfo,
    manageCoachCreateOrUpdateCoach,
    manageCoachUploadCertificateOfInsurance,
    manageCoachUploadCoachPhoto,
    manageCoachMarkCoachActiveOrInactive,
    manageCoachDeleteCoach,
    manageCoachExportCoachesList,
    manageCoachGetCoachInsuranceList,
    manageCoachGenerateInsurancePdf,
    manageCoachGetInsurancePdf,
    manageCoachUploadEditedInsurancePdf,
    manageCoachSendInsurancePdfToInsurer,
  } = ManageCoachService;
  const [coachInsuranceListData, setCoachInsuranceListData] =
    useState<CoachListWrapper>();
  const { coachGetPendingRiders, coachApproveRider, coachGetCoach } =
    CoachService;
  const [coachProfileData, setCoachProfileData] = useState<CoachListWrapper>();
  const [coachViewData, setCoachViewData] = useState<AdminCoachInfo>();
  const [downloadLoading, setDownloadLoading] = useState(false);
  const { actions } = useErrorHandlingSlice();
  const dispatch = useDispatch();
  const [pendingRidersData, setPendingRidersData] = useState<PendingRidersList>(
    {},
  );
  const [coachDetailData, setCoachDetailData] = useState<CoachInfo>();
  const handleDownloadCoach = (
    season?: string,
    teamFilter?: Array<string>,
    searchInput?: string,
    status?: string,
    sortBy?: string,
    sortOrder?: number,
    paidFilter?: number,
  ) => {
    setDownloadLoading(true);
    return new Promise((resolve, reject) => {
      handleRequest(
        manageCoachExportCoachesList(
          season,
          teamFilter,
          searchInput,
          status,
          paidFilter,
          sortBy,
          sortOrder,
        ),
      )
        .then(res => {
          resolve(res);
          setDownloadLoading(false);
          setLoading(false);
        })
        .catch(() => {
          setDownloadLoading(false);
          reject(apiError);
          setLoading(false);
        });
    });
  };
  const fetchManageGetCoaches = (
    season?: string,
    teamFilter?: Array<string>,
    searchInput?: string,
    status?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber: number = 0,
    pageSize: number = 10,
    paidFilter?: number,
  ) => {
    setLoading(true);
    let pageNumCall = pageNumber ? Math.floor(pageNumber / pageSize) + 1 : 1;
    handleRequest(
      manageCoachGetCoaches(
        season,
        teamFilter,
        searchInput,
        status,
        paidFilter,
        sortBy,
        sortOrder,
        pageNumCall,
        pageSize,
      ),
    )
      .then(res => {
        if (res) {
          setCoachProfileData(res);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };
  const deleteCoachList = (
    id?: string,
    season?: string,
    teamFilter?: string[],
    searchInput?: string,
    status?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber: number = 0,
    pageSize: number = 10,
  ) => {
    setLoading(true);
    handleRequest(manageCoachDeleteCoach(id ?? ''))
      .then(res => {
        if (res) {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Coach deleted successfully',
              show: true,
              errorType: 'success',
            }),
          );
          // Calculate the correct page after deletion
          // If current page is empty after deletion, go to previous page
          const itemsOnCurrentPage = coachProfileData?.coaches?.length ?? 0;
          let nextPageNumber = pageNumber;

          // If we're on a page with only 1 item and we just deleted it, go to previous page
          if (itemsOnCurrentPage <= 1 && pageNumber > 0) {
            nextPageNumber = pageNumber - pageSize;
          }

          fetchManageGetCoaches(
            season,
            teamFilter,
            searchInput,
            status,
            sortBy,
            sortOrder,
            nextPageNumber,
            pageSize,
          );
        }
        setLoading(false);
      })
      .catch(apiError => {
        setError(apiError);
        setLoading(false);
      });
  };

  const getCoachProfileViewData = (id: string) => {
    setCoachDataLoading(true);
    handleRequest(manageCoachGetCoachInfo(id))
      .then(res => {
        if (res) {
          setCoachViewData(res);
        }
        setCoachDataLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setCoachDataLoading(false);
      });
  };
  const CoachActiveOrInactive = (
    coachId: string,
    season?: string,
    teamFilter?: string[],
    requestBody?: ChangeCoachActiveStatusRequest,
    searchInput?: string,
    status?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber: number = 0,
    pageSize: number = 10,
    item?: string,
  ) => {
    setLoading(true);
    handleRequest(manageCoachMarkCoachActiveOrInactive(coachId, requestBody))
      .then(res => {
        if (res) {
          fetchManageGetCoaches(
            season,
            teamFilter,
            searchInput,
            status,
            sortBy,
            sortOrder,
            pageNumber,
            pageSize,
          );
          if (item === 'Make active') {
            dispatch(
              actions.dispatchToast({
                errorTxt: 'Successfully Active',
                show: true,
                errorType: 'success',
              }),
            );
          } else {
            dispatch(
              actions.dispatchToast({
                errorTxt: 'Successfully Inactive',
                show: true,
                errorType: 'success',
              }),
            );
          }
        }
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };

  const updateOrCreateCoach = (
    requestBody: Coach,
    imageFile?: Blob,
    insurenceFile?: Blob,
  ) => {
    const payload = {
      ...requestBody,
      affiliations: requestBody.affiliations?.find(
        (item: GeneralLookup) => item.id === '',
      )
        ? null
        : requestBody.affiliations,
    };
    setLoading(true);
    handleRequest(manageCoachCreateOrUpdateCoach(payload))
      .then(res => {
        if (res.code === 'EmailAlreadyInUse') {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Email is already in use',
              show: true,
              errorType: 'error',
            }),
          );
          setLoading(false);
        } else {
          if (res && res.errors) {
            // Convert the errors object to a readable string
            const errorMessages = Object.entries(res.errors)
              .map(([field, messages]) => {
                // Each field can have multiple error messages (array)
                const fieldErrors = Array.isArray(messages)
                  ? messages.join(', ')
                  : messages;
                return `${fieldErrors}`;
              })
              .join('\n');

            dispatch(
              actions.dispatchToast({
                errorTxt: errorMessages,
                show: true,
                errorType: 'error',
              }),
            );
            setLoading(false);
          } else if (res && !isApiError(res)) {
            if (insurenceFile) {
              let formData = {
                certificateOfInsurance: insurenceFile,
              };
              uploadCertificateOfInsurance(
                requestBody?.id === null ? res?.id : requestBody?.id,
                formData,
                action,
              );
            }
            if (imageFile) {
              let formData = {
                file: imageFile,
              };
              updateCoachImage(
                requestBody?.id === null ? res?.id : requestBody?.id,
                formData,
                action,
              );
            }
            if (action === 'update') {
              if (!imageFile && !insurenceFile) {
                dispatch(
                  actions.dispatchToast({
                    errorTxt: 'Coach Updated Successfully',
                    show: true,
                    errorType: 'success',
                  }),
                );
                setLoading(false);
                redirect(`/admin/coaches/view/${requestBody?.id}`);
              }
            }
            if (action === 'create') {
              if (!imageFile && !insurenceFile) {
                dispatch(
                  actions.dispatchToast({
                    errorTxt: 'Coach Added Successfully',
                    show: true,
                    errorType: 'success',
                  }),
                );
                console.log('insurenceFile');
                setLoading(false);
                redirect(`/admin/coaches/view/${res?.id}`);
              }
            }
          } else {
            dispatch(
              actions.dispatchToast({
                errorTxt: res.description,
                show: true,
                errorType: 'error',
              }),
            );
            setLoading(false);
          }
        }
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };

  const uploadCertificateOfInsurance = (
    id: string,
    formData?: {
      certificateOfInsurance?: Blob;
    },
    action?: string,
  ) => {
    setLoading(true);
    handleRequest(manageCoachUploadCertificateOfInsurance(id, formData))
      .then(res => {
        setLoading(false);
        console.log(action, 'action');
        if (action === 'create') {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Coach Added Successfully',
              show: true,
              errorType: 'success',
            }),
          );
        } else {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Coach Updated Successfully',
              show: true,
              errorType: 'success',
            }),
          );
        }
        redirect(`/admin/coaches/view/${id}`);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };

  const updateCoachImage = (
    id: string,
    formData?: {
      file?: Blob;
    },
    action?: string,
  ) => {
    setLoading(true);
    handleRequest(manageCoachUploadCoachPhoto(id, formData))
      .then(res => {
        setLoading(false);
        redirect(`/admin/coaches/view/${id}`);
        if (action === 'add') {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Coach Added Successfully',
              show: true,
              errorType: 'success',
            }),
          );
        } else {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Coach Updated Successfully',
              show: true,
              errorType: 'success',
            }),
          );
        }
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };
  const fetchApprovalPendingRiders = (
    searchName?: string,
    pageNumber: number = 0,
    pageSize: number = 10,
  ) => {
    setLoading(true);
    setError(null);
    const pageNumCall = pageNumber ? Math.floor(pageNumber / pageSize) + 1 : 1;
    handleRequest(coachGetPendingRiders(searchName, pageNumCall, pageSize))
      .then(res => {
        if (res) {
          setPendingRidersData(res);
        }
        setLoading(false);
      })
      .catch(err => {
        setError(apiError);
        setLoading(false);
        console.error('Error fetching pending riders:', err);
      });
  };
  const approvePendingRider = (
    requestBody?: ApproveRiderRequest,
    callback?: () => void,
  ) => {
    handleRequest(CoachService.coachApproveRider(requestBody))
      .then(res => {
        if (res) {
          dispatch(
            actions.dispatchToast({
              errorTxt: 'Rider Approved Successfully',
              show: true,
              errorType: 'success',
            }),
          );
          if (callback) {
            callback();
          }
        }
      })
      .catch(err => {
        console.error('Error approving rider:', err);
      });
  };
  const getSelectedCoachDetailsById = (coachId: string) => {
    setLoading(true);
    setError(null);
    handleRequest(
      coachGetCoach(coachId)
        .then(res => {
          if (res) {
            setCoachDetailData(res);
          }
        })
        .catch(error => {
          console.log(error.body, 'errorerror');
          if (error.body) {
            dispatch(
              actions.dispatchToast({
                errorTxt: error.body.description,
                show: true,
                errorType: 'error',
              }),
            );
          }
        }),
    );
  };
  const fetchCoachInsuranceList = (
    seasonId?: string,
    searchInput?: string,
    pageNumber: number = 0,
    pageSize: number = 10,
  ) => {
    setLoading(true);
    const pageNumCall = pageNumber ? Math.floor(pageNumber / pageSize) + 1 : 1;
    handleRequest(
      manageCoachGetCoachInsuranceList(
        seasonId,
        searchInput,
        pageNumCall,
        pageSize,
      ),
    )
      .then(res => {
        if (res) {
          setCoachInsuranceListData(res);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(apiError);
        setLoading(false);
      });
  };

  const generatePdf = (
    applicationId: string,
    onSuccess?: (data: InsuranceApplicationPdf) => void,
  ) => {
    setCoachDataLoading(true);
    handleRequest(manageCoachGenerateInsurancePdf(applicationId))
      .then((res: InsuranceApplicationPdf) => {
        dispatch(
          actions.dispatchToast({
            errorTxt: 'PDF generated successfully',
            show: true,
            errorType: 'success',
          }),
        );
        onSuccess?.(res);
        setCoachDataLoading(false);
      })
      .catch(() => {
        dispatch(
          actions.dispatchToast({
            errorTxt: 'Failed to generate PDF',
            show: true,
            errorType: 'error',
          }),
        );
        setError(apiError);
        setCoachDataLoading(false);
      });
  };
  const getGeneratedPdf = (
    applicationId: string,
    onSuccess?: (data: InsuranceApplicationPdf) => void,
  ) => {
    setCoachDataLoading(true);
    handleRequest(manageCoachGetInsurancePdf(applicationId))
      .then((res: InsuranceApplicationPdf) => {
        onSuccess?.(res);
        setCoachDataLoading(false);
      })
      .catch(() => {
        dispatch(
          actions.dispatchToast({
            errorTxt: 'Failed to retrieve PDF',
            show: true,
            errorType: 'error',
          }),
        );
        setError(apiError);
        setCoachDataLoading(false);
      });
  };
  const uploadInsurancePdf = (
    applicationId: string,
    formData?: {
      file?: Blob;
    },
    onSuccess?: () => void,
  ) => {
    setCoachDataLoading(true);
    handleRequest(manageCoachUploadEditedInsurancePdf(applicationId, formData))
      .then(() => {
        dispatch(
          actions.dispatchToast({
            errorTxt: 'PDF uploaded successfully',
            show: true,
            errorType: 'success',
          }),
        );
        onSuccess?.();
        setCoachDataLoading(false);
      })
      .catch(() => {
        dispatch(
          actions.dispatchToast({
            errorTxt: 'Failed to upload PDF',
            show: true,
            errorType: 'error',
          }),
        );
        setError(apiError);
        setCoachDataLoading(false);
      });
  };
  const sendMailtoInsurer = (
    applicationId: string,
    requestBody?: SendInsurancePdf,
    onSuccess?: (data: InsuranceApplicationPdf) => void,
  ) => {
    setCoachDataLoading(true);
    handleRequest(
      manageCoachSendInsurancePdfToInsurer(applicationId, requestBody),
    )
      .then((res: InsuranceApplicationPdf) => {
        dispatch(
          actions.dispatchToast({
            errorTxt: 'PDF sent to insurer successfully',
            show: true,
            errorType: 'success',
          }),
        );
        onSuccess?.(res);
        setCoachDataLoading(false);
      })
      .catch(() => {
        dispatch(
          actions.dispatchToast({
            errorTxt: 'Failed to send PDF to insurer',
            show: true,
            errorType: 'error',
          }),
        );
        setError(apiError);
        setCoachDataLoading(false);
      });
  };

  return {
    loading,
    coachProfileData,
    fetchManageGetCoaches,
    deleteCoachList,
    getCoachProfileViewData,
    coachViewData,
    CoachActiveOrInactive,
    updateOrCreateCoach,
    coachDataLoading,
    downloadLoading,
    handleDownloadCoach,
    fetchApprovalPendingRiders,
    pendingRidersData,
    approvePendingRider,
    getSelectedCoachDetailsById,
    coachDetailData,
    fetchCoachInsuranceList,
    coachInsuranceListData,
    generatePdf,
    getGeneratedPdf,
    uploadInsurancePdf,
    sendMailtoInsurer,
  };
};
export default useCoachApi;
